import { ExtraDataItem } from '@common-models/ExtraDataItem'
import type { Descendant } from 'slate'
import { userDisplayName } from '@common-models/user'
import { getCurrentBoard } from '@common-reducers/BoardsSelectors'
import { DBTasksExtraDataThunks, tasksSelectors } from "@common-reducers/DBServiceThunks"
import { extraDataActions, setEditModeByExtraDataId, setIsNewSubItemDialog } from '@common-reducers/ExtraDataReducer'
import { CreateExtraDataAndEntitySuscribersThunk } from '@common-reducers/ExtraDataThunks'
import { selectUserId } from "@common-reducers/UserSelectors";
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook'
import { ReplyOutlined, ThumbUpOutlined, VisibilityOutlined } from '@mui/icons-material'
import { IconButton, Menu, Tooltip } from '@mui/material'
import { Box, Stack } from '@mui/system'
import moment from 'moment'
import { useRef } from 'react'
import { BiPin } from 'react-icons/bi'
import { BsThreeDots } from 'react-icons/bs'
import PersonAvatar from '../person-avatar/person-avatar'
import ExtraDataMenu from '../picker-dialog/pickers/extraDataMenu'
import MessageThread from './MessageThread'
// Lazy boundary over the slate editor lib (SlateEditor + RenderUpdate now load
// slate/slate-react/slate-history on demand). Same props as the eager versions.
import SlateEditor, { RenderUpdate } from './lazy-slate'
import classes from "./editor/thread.module.scss"
import ExtraDataCreatedAtTag from './extra-data-created-at-tag'
import LikedByTooltipComponent from './liked-by-tooltip-component'
import SeenByTooltipComponent from './seen-by-tooltip-component'

type Props = {
    item: ExtraDataItem;
}

export default function ExtraDataItemComponent({
    item
}: Props) {
    const dispatch = useAppDispatch()
    const userId = useAppSelector(selectUserId)
    const editModeByExtraDataId = useAppSelector(
        (state) => state.ExtraDataReducer.editModeByExtraDataId
    );
    const activeMenuId = useAppSelector((state) => state.ExtraDataReducer.activeMenuId)
    const displayExtraDataOfTaskById = useAppSelector(
        (state) => state.ExtraDataReducer.displayExtraDataOfTaskById)

    const task = useAppSelector((state) =>
        tasksSelectors.selectById(state, displayExtraDataOfTaskById)
    );
    const currentBoard = useAppSelector(getCurrentBoard);
    const isNewSubItemDialog = useAppSelector((state) => state.ExtraDataReducer.isNewSubItemDialog)

    const menuRef = useRef(null);

    if (!item._id) {
        return null;
    }
    const itemId: string = item._id;


    // const author = {
    //     displayName: getDisplayNameFromFirstLastAndEmail(item.ownerObj?.firstName, item.ownerObj?.lastName, item.ownerObj?.email),
    //     waId: "xxxx",
    // };

    const updateConversation = (editorValue: { editorValue?: unknown } | unknown, isDraft: boolean) => {
        if (isDraft) return;
        const _extraDataItem: ExtraDataItem = {
            _id: item._id,
            extraData: (editorValue as { editorValue?: Descendant[] }).editorValue,
        };

        dispatch(DBTasksExtraDataThunks.patch({
            entity: _extraDataItem
        }));
        dispatch(setEditModeByExtraDataId(""))
    }

    const cancelUpdate = () => {
        dispatch(setEditModeByExtraDataId(""))
    }

    const closeMenu = () => {
        dispatch(extraDataActions.setActiveMenuId(""))
    }

    const likeExtraDataItem = () => {
        if (!userId) return;
        let newIsLiked = true
        if (item?.isLiked?.[userId] === true) {
            newIsLiked = false
        }
        dispatch(DBTasksExtraDataThunks.patch({
            entity: {
                ...item,
                isLiked: {
                    ...item?.isLiked,
                    [userId]: newIsLiked,
                }
            }
        }))
    }

    const likesCounter = () => {
        const likedByIdArray = Object.entries(item?.isLiked ?? {})
            .filter(([id, value]) => value === true)
            .map(([id]) => id) ?? [];

        return (
            <>
                {likedByIdArray.length > 0 && (
                    <Tooltip title={<LikedByTooltipComponent likedByIdArray={likedByIdArray} />}>
                        <Stack direction='row'>
                            <IconButton style={{ cursor: 'default' }}>
                                <ThumbUpOutlined />
                            </IconButton>
                            <span style={{ lineHeight: '37px' }}>{likedByIdArray?.length}</span>
                        </Stack>
                    </Tooltip>
                )}
            </>
        )
    }

    const seenCounter = () => {


        return (
            <Tooltip title={<SeenByTooltipComponent seenByIdArray={item?.seenByIdArray ?? []} />}>
                <Stack direction='row'>
                    <VisibilityOutlined />
                    <span>&nbsp; {(item?.seenByIdArray ?? []).length}</span>
                    <span>&nbsp; seen</span>
                </Stack>
            </Tooltip>
        )
    }

    function createNewSubExtraData(editorValue: unknown) {

        dispatch(CreateExtraDataAndEntitySuscribersThunk({
            editorValue,
            taskId: displayExtraDataOfTaskById,
        }))

        dispatch(setIsNewSubItemDialog(''));

    }

    return (
        <div
            style={{
                borderTopRightRadius: item.isPinedToTop ? 0 : "15px",
                marginBottom: "20px",
                backgroundColor: 'var(--color-surface)',
                boxShadow: "0 2px 7px -2px rgb(0 0 0 / 15%)",
                borderRadius: "14px",
                position: "relative",
                background: "var(--color-surface)"

            }}
        >
            {item.isPinedToTop && (
                <div className={classes.pinned_badge}>

                    <BiPin />

                </div>
            )}

            <Box className={classes.thread_header}>
                <div className={classes.author_profile}>
                    <Box sx={{ marginInlineEnd: 1 }}>
                        <PersonAvatar
                            size={24}
                            email={item.profile?.email}
                            profileImg={item.profile?.profileImg}
                            lastName={item.profile?.lastName}
                            firstName={item.profile?.firstName}
                            toolTip={true}
                            isName={false}

                        />
                    </Box>
                    <Stack spacing={0.2} sx={{ fontFamily: "Inter" }}>
                        <div style={{ color: "var(--color-primary)", fontSize: '12px', fontWeight: "600" }} >
                            {userDisplayName(item.profile)}
                        </div>

                        <div style={{ color: "var(--color-primary)", fontSize: '11px', opacity: 0.3 }} >
                            {item?.updatedAt ? moment(item.updatedAt).format("DD/MM/yyyy") : moment(new Date()).format("DD/MM/yyyy")}
                        </div>

                    </Stack>
                    {/* <p className={classes.active_status}></p> */}
                </div>
                <Stack direction="row" spacing={1} className={classes.aside_right} >
                    <ExtraDataCreatedAtTag createdAt={item?.createdAt} />
                    <button
                        ref={menuRef}
                        id={`extraData_${itemId}`} key={`extraData_${itemId}`}
                        className={classes.drop_down_btn}
                        onClick={() => {
                            if (activeMenuId !== itemId) {
                                dispatch(extraDataActions.setActiveMenuId(itemId))
                            } else {
                                dispatch(extraDataActions.setActiveMenuId(""))
                            }
                        }}
                    >
                        <BsThreeDots />
                    </button>
                </Stack>
                <Menu
                    id="basic-menu"
                    anchorEl={menuRef.current}
                    open={activeMenuId === item._id}
                    onClose={closeMenu}
                    MenuListProps={{
                        'aria-labelledby': 'basic-button',
                    }}
                    PaperProps={{
                        sx: {
                            overflow: 'visible',
                            filter: 'drop-shadow(0px 0px 2px rgba(0,0,0,0.32))',
                            mt: 1,
                            "& .MuiList-root": {
                                padding: 0
                            },
                            '& .MuiAvatar-root': {
                                width: 32,
                                height: 32,
                                ml: -0.5,
                                mr: 1,
                            },
                            '&::before': {
                                content: '""',
                                display: 'block',
                                position: 'absolute',
                                top: 0,
                                right: 40,
                                width: 10,
                                height: 10,
                                bgcolor: 'background.paper',
                                transform: 'translateY(-50%) rotate(45deg)',
                                zIndex: 0,
                            },

                        },

                    }}
                    sx={{ padding: 0 }}
                >
                    <ExtraDataMenu extraDataId={item._id} />
                </Menu>
            </Box>

            {editModeByExtraDataId === item._id ? (
                <SlateEditor value={item.extraData} onValueUpdate={updateConversation} addBorder={false} onCancel={cancelUpdate} />
            ) : (
                <Box className={classes.thread_main_component} sx={{ fontFamily: "'Inter', 'Assistant'" }}>
                    <RenderUpdate update={item.extraData} />
                    {currentBoard?.displayBetaFeatures &&
                        <Stack direction='row' sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                            height: '40px',

                        }}>
                            <div>{likesCounter()}</div>
                            <div>{seenCounter()}</div>
                        </Stack>
                    }
                    {isNewSubItemDialog === item?._id &&
                        <SlateEditor
                            value={null}
                            onValueUpdate={createNewSubExtraData}
                            addBorder={true}
                            parentItemId={item?._id} />
                    }
                    {currentBoard?.displayBetaFeatures &&
                        <Stack direction='row' sx={{ width: '100%', justifyContent: 'space-between', marginTop: '15px' }}>
                            <IconButton onClick={() => { likeExtraDataItem() }}
                                sx={{
                                    width: '49%',
                                    boxSizing: 'border-box',
                                    border: '1px solid var(--tasks-cell-border)',
                                    borderRadius: '8px',
                                    backgroundColor: userId && item?.isLiked?.[userId] ? 'var(--tasks-group-bg)' : 'white',
                                }}>
                                <ThumbUpOutlined />
                            </IconButton>
                            <IconButton onClick={() => { dispatch(setIsNewSubItemDialog(itemId)) }}
                                sx={{
                                    width: '49%',
                                    boxSizing: 'border-box',
                                    border: '1px solid var(--tasks-cell-border)',
                                    borderRadius: '8px'
                                }}>
                                <ReplyOutlined />
                            </IconButton>
                        </Stack>
                    }
                    <MessageThread parentItemId={item?._id} />
                </Box>
            )}
        </div>
    )
}